import React from 'react';

import { twMerge } from 'tailwind-merge';

import type { FormikProps } from 'formik';
import { Form, Formik } from 'formik';

import * as Yup from 'yup';

import Button from 'components/button/button';
import Dialog from 'components/dialog/dialog';
import Divider from 'components/divider/divider';
import Icon from 'components/icon/icon';
import Typography from 'components/typography/typography';
import { createLocale } from 'locales';

import { useOrganizationWeekPlanner } from 'state/context/organization/OrganizationWeekPlanner';
import TextAreaField from 'form-elements/text-area-field';

const localize = createLocale('organizationWeekPlannerLocales');
const globalLocalize = createLocale('global');
interface CancelBulkJobsDialogProps {
  isOpen?: boolean;
  onAcceptClicked?: (reason?: string) => void;
  onBackClicked?: () => void;
  onCloseClicked?: () => void;
  variant?: 'compensate';
}

const CancelDialogSchema = Yup.object().shape({
  reason: Yup.string()
    .required(globalLocalize('fieldRequiredErrorMessage'))
    .min(4, localize('minMessageLengthErrorMessage', { count: 4 }))
    .max(250, localize('maxMessageLengthErrorMessage', { count: 250 })),
});

const CancelBulkJobsDialog: React.FC<CancelBulkJobsDialogProps> = (props) => {
  const { isOpen, onAcceptClicked, onBackClicked, variant } = props;

  const { tableParams } = useOrganizationWeekPlanner();

  const paddingClassName = twMerge('py-4 px-7');

  const headerClassNames = twMerge(
    'flex flex-row justify-between w-full',
    paddingClassName,
  );

  const bodyClassNames = twMerge(
    'flex flex-col items-center gap-y-6 w-full',
    paddingClassName,
  );

  const footerClassNames = twMerge(
    'flex flex-row justify-end w-full gap-x-4',
    paddingClassName,
  );

  const titleClassNames = twMerge('text-purple');

  return (
    <Dialog
      className="flex justify-center items-center max-w-2xl"
      isOpen={isOpen}
      onClose={() => props.onCloseClicked?.()}
    >
      <div id="header" className={headerClassNames}>
        <Typography variant="page-heading" as="p" className={titleClassNames}>
          {localize('jobBulkCancel', {
            count: tableParams.numberOfSelectedJobs,
          })}
        </Typography>
        <button type="button" onClick={() => props.onCloseClicked?.()}>
          <Icon icon="close" />
        </button>
      </div>
      <Divider orientation="horizontal" className="w-full" />
      <Formik
        initialValues={{ reason: '' }}
        validationSchema={CancelDialogSchema}
        onSubmit={(values) => {
          onAcceptClicked?.(values.reason.trim());
        }}
      >
        {(_props: FormikProps<{ reason: string }>) => (
          <Form>
            <div id="body" className={bodyClassNames}>
              {variant === 'compensate' && (
                <div className="flex py-4 pl-1 pr-4 gap-x-2 rounded-md border-[1.6px] border-tomato-red">
                  <Icon
                    icon="ant-design_warning-filled"
                    className="text-tomato-red"
                    size={45}
                  />
                  <div className="flex flex-col">
                    <Typography
                      as="p"
                      variant="body-large-bold"
                      className="text-gray700"
                    >
                      {localize('lateCancellationCompensation')}
                    </Typography>
                    <Typography
                      as="p"
                      variant="body-medium"
                      className="text-gray500"
                    >
                      {localize('compensateMessage')}
                    </Typography>
                  </div>
                </div>
              )}
              <div className="flex flex-col text-center items-center gap-y-2">
                <Typography as="p" variant="page-subheading">
                  {localize('areYouSureYouWantToContinue')}
                </Typography>
                <Typography as="p" variant="body-medium">
                  {localize('cancelBulkDialogMessage')}
                </Typography>
              </div>
              <TextAreaField
                rows={5}
                textareaClassName="w-full resize-none"
                placeholder={localize('reasonOfCanceling')}
                name="reason"
              />
            </div>
            <Divider orientation="horizontal" className="w-full" />
            <div id="footer" className={footerClassNames}>
              <Button
                variant="outlined"
                label={localize('goBack')}
                onClick={onBackClicked}
              />
              <Button
                variant="danger"
                label={localize('yesCancelJob')}
                type="submit"
              />
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CancelBulkJobsDialog;
